import $$ from 'jquery'
// .scss, not .css: webpack routes .scss through MiniCssExtractPlugin (extracted
// into exolog-loader.css, which the page already links) while plain .css uses
// style-loader's RUNTIME injection — and that runtime is not present in the
// production bundle, so a .css import ships its text but never inserts it. The
// widget then mounted correctly with no styling at all and looked like a plain
// file input.
import './uploader.scss'

/**
 * Exolog uploader — async file upload for public form file fields.
 *
 * Each file is sent the moment it is picked, so a rejected file is reported on
 * its own row within a second instead of after the visitor has filled in and
 * submitted the whole form.
 *
 * Every uploaded id is mirrored into a hidden `__<field>[]` input, which is the
 * shape UploadFiles::collectRetained() already understands — the submit path is
 * unchanged.
 *
 * Config and ALL text arrive in one `data-exo-uploader` JSON blob written by
 * FFQT\File. exo_editor has no i18n layer, so any string not passed from PHP
 * would have to be hardcoded English.
 */

var uid = 0

export default {
    init() {
        // The widget mounts in the page editor TOO, but inert: it renders exactly
        // what a visitor sees so the layout can be judged in place, while picking
        // and uploading are disabled. Uploading here would attach files to a react
        // the editor is not submitting, leaving orphans on disk.
        //
        // It used to skip the editor entirely, which meant an admin saw a raw
        // browser input in edit mode and the widget only in preview — no way to
        // see the real control while laying out the form.
        $$('input[type="file"][data-exo-uploader]').each((i, el) => this.mount(el))
    },

    /**
     * Is the page open in the Exolog editor?
     *
     * Same signal the loader uses to decide whether to boot the editor at all
     * (`meta[name="exologEditor"]`), so the widget can never disagree with it.
     */
    inEditor() {
        return $$('meta[name="exologEditor"]').length > 0
    },

    mount(el) {
        const $input = $$(el)
        const cfg = $input.data('exoUploader')      // jQuery parses the JSON blob

        if (!cfg || !cfg.uploadUrl || !cfg.field) {
            return                                   // never render a widget that cannot upload
        }

        // The shell is built HERE rather than in a blade. Which template renders a
        // file field is environment-dependent — the themed `file.blade.php` on one
        // setup, Bootstrap's `group/input/file.blade.php` (which wraps the input in
        // `.custom-file` and never reaches our @include) on another. Same code, same
        // files, different markup, so a server-rendered shell appeared locally and
        // silently vanished on live. Building it in JS removes that dependency
        // entirely: the widget looks the same on every theme, grouped or not.
        //
        // A server-rendered shell is still honoured when present, so a page that
        // does render the partial keeps working (and keeps its no-JS fallback).
        let $root = $input.closest('.form-group').find('[data-exo-uploader-root]').first()
        if (!$root.length) {
            $root = this.buildShell($input, cfg)
        }
        if (!$root.length) {
            return
        }

        const state = {
            files: [],
            queue: [],
            busy: false,
            cfg: {
                field: cfg.field,
                url: cfg.uploadUrl,
                maxFiles: parseInt(cfg.maxFiles, 10) || 1,
                maxBytes: cfg.maxSize ? parseFloat(cfg.maxSize) * 1024 * 1024 : null,
                types: (cfg.types || []).map(t => String(t).toLowerCase()),
                required: !!cfg.required,
                labels: cfg.labels || {},
                labelUrl: cfg.labelUrl || null,   // admin-only; absent for visitors
            },
            // Inert mode: rendered, but picking/uploading are off.
            editing: this.inEditor(),
        }

        const ctx = {
            el, $input, $root, state,
            $list: $root.find('[data-exo-uploader-list]').first(),
            $add: $root.find('[data-exo-uploader-add]').first(),
            $counter: $root.find('[data-exo-uploader-counter]').first(),
            $notice: $root.find('[data-exo-uploader-notice]').first(),
            $ids: $root.find('[data-exo-uploader-ids]').first(),
        }

        if (state.editing) {
            $root.addClass('exo-uploader--editing')
        }

        // Marks the input as "a widget is standing in for me". The stylesheet hides
        // the native control only when this is set, so a field whose widget did
        // not mount (edit mode, bad config) still shows its normal input rather
        // than nothing at all.
        $input.attr('data-exo-mounted', '')

        // Every mounted field is registered so a toolbar Save can flush a pending
        // rename on ANY of them, not just the one that happens to be focused.
        this.mounted = this.mounted || []
        this.mounted.push(ctx)

        this.seedExisting(ctx)
        this.bind(ctx)
        this.render(ctx)
    },

    /**
     * Create the widget shell next to the input.
     *
     * Inserted AFTER `.custom-file` when Bootstrap's group wrapper is present:
     * that wrapper has a fixed height and absolutely-positioned children, so a
     * widget rendered inside it gets clipped to a single line. Placing it as a
     * sibling avoids the whole class of override CSS that used to be needed.
     *
     * Markup mirrors partials/exo-uploader.blade.php: a real <ol>, a native
     * <progress> and a real <button> whose disabled state is an attribute, so it
     * stays usable with no CSS at all.
     */
    buildShell($input, cfg) {
        const field = cfg.field
        const labels = cfg.labels || {}

        const $root = $$('<div class="exo-uploader" data-exo-uploader-root></div>')
            .attr('data-for', $input.attr('id') || '')

        $$('<ol class="exo-uploader__list" data-exo-uploader-list></ol>').appendTo($root)

        const $foot = $$('<div class="exo-uploader__foot"></div>').appendTo($root)
        $$('<button type="button" class="exo-uploader__add" data-exo-uploader-add></button>')
            .text(labels.choose || '')
            .appendTo($foot)
        $$('<p class="exo-uploader__counter" data-exo-uploader-counter aria-live="polite"></p>')
            .appendTo($foot)

        $$('<p class="exo-uploader__notice" data-exo-uploader-notice role="status"></p>')
            .appendTo($root)

        // Retained ids. The EMPTY SENTINEL must always be present: the server reads
        // an absent `__field` key as "keep whatever is stored", so without it
        // removing every file would silently undo itself.
        const $ids = $$('<div class="exo-uploader__ids" data-exo-uploader-ids></div>')
            .appendTo($root)
        $$('<input type="hidden" />').attr('name', '__' + field + '[]').val('').appendTo($ids)

        // Sibling of the wrapper, not a child of it (see above).
        const $wrap = $input.closest('.custom-file')
        if ($wrap.length) {
            // Bootstrap's fake "Choose file" label also prints the chosen filename,
            // which duplicates the widget's own list. The stylesheet hides it via
            // :has(), which is not supported everywhere — do it here too.
            $wrap.find('.custom-file-label').hide()
            $root.insertAfter($wrap)
        } else {
            $root.insertAfter($input)
        }

        return $root
    },

    /**
     * Adopt the rows the server rendered for files already on the react. They
     * are real markup so the field still works with JS off; here they become
     * state so removal and the counter behave like any other row.
     */
    /**
     * Load the files already on the react.
     *
     * Two sources, because the shell can come from either place:
     *  - server-rendered rows (`<li data-exo-file-id>`) when a blade drew the shell
     *  - the field's own retained-id inputs (`__<field>[]`) otherwise, which the
     *    multi-file blade renders regardless of which template drew the input
     *
     * Without the second source a JS-built shell would start empty on an EDIT and
     * the visitor's existing files would silently drop off the react on save.
     */
    seedExisting(ctx) {
        const seen = {}

        ctx.$list.find('[data-exo-file-id]').each((i, li) => {
            const $li = $$(li)
            const id = $li.attr('data-exo-file-id')
            seen[id] = true
            ctx.state.files.push({
                uid: ++uid,
                id: id,
                name: $li.find('[data-exo-name]').text() || id,
                size: $li.attr('data-exo-size') || '',
                status: 'stored',
                progress: 1,
            })
        })

        // Retained ids rendered anywhere in this field's form-group. The empty
        // sentinel is skipped; ids already seeded from a row are not duplicated.
        const sel = 'input[type="hidden"][name="__' + ctx.state.cfg.field + '[]"]'
        ctx.$input.closest('.form-group').find(sel).each((i, node) => {
            const $n = $$(node)
            const id = $n.val()

            // The ids container this widget owns is rebuilt from state on every
            // render, so reading from it would double-count.
            if (!id || seen[id] || $n.closest('[data-exo-uploader-ids]').length) {
                return
            }
            seen[id] = true

            ctx.state.files.push({
                uid: ++uid,
                id: id,
                name: id.split('/').pop() || id,
                size: '',
                status: 'stored',
                progress: 1,
            })

            // This widget now owns the id; drop the original so it is not posted twice.
            $n.remove()
        })
    },

    /**
     * Inline rename of the upload button, in the page editor.
     *
     * Click the button, type, blur or press Enter to save. The value goes to the
     * field's `button_label` custom param, so the form builder and the page agree
     * on one stored setting rather than each keeping its own.
     *
     * Escape reverts. An empty value clears the override and the button falls back
     * to the translated default, which is why the placeholder shows that default
     * rather than leaving the box blank.
     */
    bindLabelEditing(ctx) {
        const url = ctx.state.cfg.labelUrl

        // No URL means this visitor is not an admin (the server only emits it for
        // admins). Render the preview, but no rename affordance at all.
        if (!url) {
            ctx.$add.attr('disabled', 'disabled')
            return
        }

        // The button must be clickable to start editing, so it cannot carry the
        // `disabled` attribute here. Picking is prevented by bind() returning
        // before the picker handlers are attached, not by the attribute.
        ctx.$add
            .addClass('exo-uploader__add--editable')
            .attr('title', ctx.state.cfg.labels.renameHint || '')

        ctx.$add.on('click', (e) => {
            e.preventDefault()
            e.stopPropagation()          // the editor binds its own click handlers
            this.startLabelEdit(ctx, url)
        })
    },

    startLabelEdit(ctx, url) {
        if (ctx.$root.find('[data-exo-label-input]').length) {
            return                        // already editing
        }

        const current = ctx.$add.text()
        const $field = $$('<input type="text" class="exo-uploader__labelinput" data-exo-label-input />')
            .attr('placeholder', ctx.state.cfg.labels.choose || '')
            .val(current)

        ctx.$add.hide().after($field)
        $field.trigger('focus').trigger('select')

        let done = false
        const finish = (keep) => {
            if (done) {
                return                    // blur fires again when we remove the field
            }
            done = true

            const next = keep ? $field.val().trim() : current
            $field.remove()
            ctx.$add.show()

            if (keep && next !== current) {
                this.stageLabel(ctx, next)
            }
        }

        $field.on('keydown', (e) => {
            if (e.key === 'Enter') {
                e.preventDefault()
                finish(true)
            } else if (e.key === 'Escape') {
                e.preventDefault()
                finish(false)
            }
        })
        $field.on('blur', () => finish(true))

        // Typing a label must not reach the editor's own key handling.
        $field.on('keypress keyup click', (e) => e.stopPropagation())
    },

    /**
     * Hold the new label as a PENDING change and hand the editor its dirty state.
     *
     * Deliberately does not save. Every other editable thing on the page (TinyMCE,
     * Tiptap, the file editor) marks the toolbar dirty and waits for Save, so a
     * rename that wrote itself the moment you clicked away would be the one
     * control on the page that cannot be reconsidered — and the red dot would
     * never appear for it.
     *
     * The button shows the new text straight away so the preview stays truthful;
     * it is the PERSIST that waits.
     */
    stageLabel(ctx, label) {
        // `savedLabel` is what the server currently holds. Typing a new name and
        // then typing the old one back leaves nothing to save, so the dot clears
        // rather than inviting a save that would be a no-op.
        if (ctx.savedLabel === undefined) {
            ctx.savedLabel = ctx.state.cfg.labels.choose || ''
        }

        ctx.$add.text(label || ctx.state.cfg.labels.choose || '')

        if (label === ctx.savedLabel) {
            ctx.pendingLabel = null
            if (!this.anyPending()) {
                $$(document).trigger('ff-uploader-dirty', [false])
            }
            return
        }

        ctx.pendingLabel = label

        // Claim the save button, then mark it dirty. Activation resets the button
        // to a clean state, so the order matters — dirty first would be wiped.
        $$(document).trigger('ff-uploader-activated')
        $$(document).trigger('ff-uploader-dirty', [true])

        this.armSaveListener()
    },

    anyPending() {
        return (this.mounted || []).some(c => c.pendingLabel !== undefined && c.pendingLabel !== null)
    },

    /**
     * One document-level listener, however many uploader fields are on the page.
     *
     * Bound lazily on the first staged rename rather than at init: a page with no
     * pending rename should not be listening for a save it has nothing to do for.
     */
    armSaveListener() {
        if (this.saveArmed) {
            return
        }
        this.saveArmed = true

        // The toolbar fires this from its own Save handler, so the rename is
        // persisted by the same click that saves everything else.
        $$(document).on('ff-uploader-save', () => this.flushPending())

        // A rename box still open when Save is pressed has not staged its value
        // yet. mousedown fires before the click handler, so committing here means
        // the value is pending by the time the save runs.
        $$(document).on('mousedown.exoUploaderSave', '.mce-btn-save', () => {
            $$('[data-exo-label-input]').trigger('blur')
        })
    },

    /**
     * Persist every staged rename on the page.
     */
    flushPending() {
        (this.mounted || []).forEach((ctx) => {
            if (ctx.pendingLabel === undefined || ctx.pendingLabel === null) {
                return
            }

            const label = ctx.pendingLabel
            const url = ctx.state.cfg.labelUrl
            ctx.pendingLabel = null

            if (!url) {
                return
            }

            $$.ajax({
                url: url,
                method: 'POST',
                data: JSON.stringify({ label: label }),
                contentType: 'application/json',
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
            }).done((res) => {
                // The server trims and strips characters that would break the
                // param string, so show what was actually STORED rather than what
                // was typed — otherwise the button lies until the next page load.
                const stored = (res && typeof res.label === 'string') ? res.label : label
                ctx.state.cfg.labels.choose = stored || ctx.state.cfg.labels.choose
                ctx.savedLabel = stored
                ctx.$add.text(stored || ctx.state.cfg.labels.choose || '')
            }).fail(() => {
                this.notice(ctx, ctx.state.cfg.labels.renameFailed || 'Could not save the label.')
                // Keep it pending so the next Save retries rather than losing the
                // rename, and put the dirty dot back — the toolbar cleared it
                // optimistically when Save was pressed, but nothing was saved.
                ctx.pendingLabel = label
                $$(document).trigger('ff-uploader-activated')
                $$(document).trigger('ff-uploader-dirty', [true])
            })
        })
    },

    bind(ctx) {
        // In the editor the widget is a PREVIEW. Picking is off, and the button
        // instead becomes an inline text field for renaming it — the same value
        // the form builder writes to `button_label`, edited from the page.
        if (ctx.state.editing) {
            this.bindLabelEditing(ctx)
            return
        }

        // The native input is the file picker; the visible button proxies to it.
        ctx.$add.on('click', (e) => {
            e.preventDefault()
            ctx.el.value = ''            // re-picking the same file must still fire change
            ctx.$input.trigger('click')
        })

        ctx.$input.on('change', (e) => {
            const picked = Array.prototype.slice.call(e.target.files || [])
            if (picked.length) {
                this.onPick(ctx, picked)
            }
            ctx.el.value = ''
        })

        ctx.$root.on('click', '[data-exo-remove]', (e) => {
            e.preventDefault()
            this.onRemove(ctx, parseInt($$(e.currentTarget).attr('data-exo-remove'), 10))
        })
    },

    /**
     * Accept up to the remaining slots and say which files were skipped, rather
     * than rejecting the whole selection — picking six for five slots should not
     * cost the visitor the five that do fit.
     */
    onPick(ctx, picked) {
        const { cfg } = ctx.state
        const remaining = Math.max(0, cfg.maxFiles - ctx.state.files.length)
        const accepted = picked.slice(0, remaining)
        const skipped = picked.length - accepted.length

        this.notice(ctx, skipped > 0
            ? this.t(ctx, 'skipped', { skipped: skipped, max: cfg.maxFiles })
            : '')

        accepted.forEach((file) => {
            const entry = {
                uid: ++uid,
                id: null,
                name: file.name,
                size: this.humanSize(file.size),
                status: 'uploading',
                progress: 0,
                file: file,
                xhr: null,
            }

            const err = this.validate(ctx, file)
            if (err) {
                entry.status = 'error'
                entry.error = err
                ctx.state.files.push(entry)
                return
            }

            ctx.state.files.push(entry)
            ctx.state.queue.push(entry)
        })

        this.render(ctx)
        this.pump(ctx)
    },

    /**
     * Mirror of FileUploadValidator so a doomed file is refused before it costs
     * an upload. The server re-checks everything — this is feedback, not a gate.
     */
    validate(ctx, file) {
        const { cfg } = ctx.state
        const ext = String(file.name).split('.').pop().toLowerCase()

        if (cfg.types.length && cfg.types.indexOf(ext) === -1) {
            return this.t(ctx, 'typeDenied') || this.t(ctx, 'failed')
        }
        if (cfg.maxBytes && file.size > cfg.maxBytes) {
            return this.t(ctx, 'tooLarge') || this.t(ctx, 'failed')
        }
        return null
    },

    /**
     * Strictly one upload at a time. The endpoint allows 30 requests a minute per
     * IP, and a shared office address picking five files twice would blow that;
     * a 429 mid-batch is also indistinguishable from a real failure to a visitor.
     */
    pump(ctx) {
        if (ctx.state.busy) return

        const entry = ctx.state.queue.shift()
        if (!entry) {
            this.syncValidity(ctx)
            return
        }

        ctx.state.busy = true
        this.upload(ctx, entry, () => {
            ctx.state.busy = false
            this.pump(ctx)
        })
    },

    upload(ctx, entry, done) {
        const form = new FormData()
        form.append('file', entry.file, entry.name)

        const xhr = new XMLHttpRequest()
        entry.xhr = xhr
        xhr.open('POST', ctx.state.cfg.url, true)
        xhr.setRequestHeader('Accept', 'application/json')

        // Update the bar directly rather than re-rendering: this fires many times
        // a second and a full re-render would thrash the DOM.
        xhr.upload.onprogress = (e) => {
            if (!e.lengthComputable) return
            entry.progress = e.loaded / e.total
            const row = ctx.$list.find('[data-exo-uid="' + entry.uid + '"]')
            const bar = row.find('progress')[0]
            if (bar) bar.value = entry.progress
            row.find('[data-exo-status]').text(
                this.t(ctx, 'uploading') + ' ' + Math.round(entry.progress * 100) + '%'
            )
        }

        xhr.onload = () => {
            let body = {}
            try { body = JSON.parse(xhr.responseText || '{}') } catch (e) { /* non-JSON */ }

            if (xhr.status >= 200 && xhr.status < 300 && body.id) {
                entry.id = body.id
                entry.name = body.name || entry.name
                entry.status = 'done'
                entry.progress = 1
            } else if (xhr.status === 429) {
                // Rate-limited: stop the queue rather than draining it into more
                // 429s, and mark what is left so nothing looks silently stuck.
                entry.status = 'error'
                entry.error = body.error || this.t(ctx, 'failed')
                ctx.state.queue.forEach((q) => {
                    q.status = 'error'
                    q.error = body.error || this.t(ctx, 'failed')
                })
                ctx.state.queue.length = 0
            } else {
                entry.status = 'error'
                entry.error = body.error || this.t(ctx, 'failed')
            }

            entry.file = null
            entry.xhr = null
            this.render(ctx)
            done()
        }

        // A network-level failure (blocked cross-origin, DNS, offline) never
        // reaches the server, so there is no server message to show. Say so in
        // the console: reporting it as a plain upload failure sent me hunting
        // through server logs for a request that had never been sent.
        xhr.onerror = () => {
            if (window.console && console.warn) {
                console.warn('[exo-uploader] request failed before reaching the server',
                    { url: ctx.state.cfg.url, file: entry.name })
            }
            entry.status = 'error'
            entry.error = this.t(ctx, 'failed')
            entry.file = null
            entry.xhr = null
            this.render(ctx)
            done()
        }

        xhr.send(form)
    },

    onRemove(ctx, rowUid) {
        const idx = ctx.state.files.findIndex(f => f.uid === rowUid)
        if (idx === -1) return

        const entry = ctx.state.files[idx]

        if (entry.xhr) {
            entry.xhr.abort()                       // in flight: cancel it
        } else if (entry.status === 'done' && entry.id) {
            this.revert(ctx, entry.id)              // uploaded now: delete the orphan
        }
        // 'stored' files are left on disk — they may be referenced elsewhere;
        // dropping the hidden input is what un-retains them.

        ctx.state.files.splice(idx, 1)
        ctx.state.queue = ctx.state.queue.filter(q => q.uid !== rowUid)
        this.render(ctx)
    },

    revert(ctx, id) {
        const xhr = new XMLHttpRequest()
        xhr.open('DELETE', ctx.state.cfg.url, true)
        xhr.setRequestHeader('Content-Type', 'text/plain')
        xhr.send(id)                                 // the endpoint reads the raw body
    },

    render(ctx) {
        const { cfg, files } = ctx.state

        ctx.$list.empty()
        files.forEach(f => ctx.$list.append(this.row(ctx, f)))

        // In the editor the button is a rename affordance, not a picker, so the
        // file-count limit must not disable it — a field already at its limit
        // would otherwise become unrenameable.
        ctx.$add.prop('disabled', ctx.state.editing
            ? !cfg.labelUrl                      // non-admin: inert, nothing to do
            : files.length >= cfg.maxFiles)
        ctx.$counter.text(this.t(ctx, 'counter', { count: files.length, max: cfg.maxFiles }))

        this.syncIds(ctx)
        this.syncValidity(ctx)
    },

    row(ctx, f) {
        const status = f.status === 'error' ? (f.error || this.t(ctx, 'failed'))
            : f.status === 'uploading' ? this.t(ctx, 'uploading')
            : f.status === 'stored' ? this.t(ctx, 'stored')
            : this.t(ctx, 'uploaded')

        const ext = String(f.name).split('.').pop().toUpperCase().slice(0, 4)

        const $li = $$('<li class="exo-uploader__item"></li>')
            .attr('data-exo-uid', f.uid)
            .attr('data-status', f.status)

        $$('<span class="exo-uploader__icon" aria-hidden="true"></span>').text(ext).appendTo($li)
        $$('<span class="exo-uploader__name" data-exo-name></span>').text(f.name).appendTo($li)
        $$('<span class="exo-uploader__size"></span>').text(f.size || '').appendTo($li)

        const $wrap = $$('<span class="exo-uploader__statuswrap"></span>').appendTo($li)
        $$('<span class="exo-uploader__status" data-exo-status></span>').text(status).appendTo($wrap)

        if (f.status !== 'error') {
            $$('<progress class="exo-uploader__progress" max="1"></progress>')
                .attr('value', f.progress || 0).appendTo($wrap)
        }

        $$('<button type="button" class="exo-uploader__remove"></button>')
            .attr('data-exo-remove', f.uid)
            .attr('aria-label', this.t(ctx, 'remove') + ' ' + f.name)
            .text('×')
            .appendTo($li)

        return $li
    },

    /**
     * Rebuild the retained-id inputs from state.
     *
     * The EMPTY SENTINEL is emitted first, unconditionally, on every rebuild:
     * collectRetained() reads an absent `__field` key as "keep whatever is
     * stored", so without it removing every file would silently undo itself.
     */
    syncIds(ctx) {
        const name = '__' + ctx.state.cfg.field + '[]'
        ctx.$ids.empty()

        ctx.$ids.append($$('<input type="hidden">').attr('name', name).val(''))

        ctx.state.files.forEach((f) => {
            if (f.id) {
                ctx.$ids.append($$('<input type="hidden">').attr('name', name).val(f.id))
            }
        })
    },

    /**
     * A required file field is EMPTY at submit under async upload — the files are
     * in hidden inputs. The resolver strips the `required` attribute so the form
     * is submittable at all; this re-enforces it through the native constraint
     * API, so checkValidity() in ExoForm keeps working with no changes there.
     */
    syncValidity(ctx) {
        const { cfg, files } = ctx.state

        // The editor is not submitting this form. Marking a required file field
        // invalid there would only put a browser validation bubble in an admin's
        // way on a control they cannot satisfy (picking is off).
        if (ctx.state.editing) {
            ctx.el.setCustomValidity('')
            return
        }

        const busy = files.some(f => f.status === 'uploading')
        const has = files.some(f => f.status === 'done' || f.status === 'stored')

        if (busy) {
            ctx.el.setCustomValidity(this.t(ctx, 'busy'))
        } else if (cfg.required && !has) {
            ctx.el.setCustomValidity(this.t(ctx, 'required'))
        } else {
            ctx.el.setCustomValidity('')
        }
    },

    notice(ctx, msg) {
        ctx.$notice.text(msg || '')
    },

    t(ctx, key, params) {
        let s = ctx.state.cfg.labels[key]
        if (s === undefined || s === null || s === '') return ''
        Object.keys(params || {}).forEach((k) => {
            s = s.split(':' + k).join(params[k])
        })
        return s
    },

    humanSize(bytes) {
        if (!bytes && bytes !== 0) return ''
        if (bytes < 1024) return bytes + ' B'
        if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + ' kB'
        return (bytes / 1024 / 1024).toFixed(1).replace('.', ',') + ' MB'
    },
}
